import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    ArrowBack,
    DeleteOutline,
    EditOutlined,
    ExpandLess,
    ExpandMore,
} from "@mui/icons-material";
import { type DropResult } from "react-beautiful-dnd";
import RubricManagementList, { Column, type StatusFilter } from "../../molecules/RubricManagementList";
import { useListState } from "../../../hooks/useListState";
import QuestionsSection from "./CodeWorkflow/QuestionsSection";
import {
    type CodeWorkflow,
    type Question,
    type WorkflowQuestion,
    type CodeWorkflowType,
    WorkflowTriggerAction,
    QuestionType,
} from "../../../types/rubric";
import type { AnswerValue } from "../../../types/answerSet";
import {
    listCodeWorkflowsPaginated,
    fetchCodeWorkflow,
    createCodeWorkflow,
    updateCodeWorkflow,
    deleteCodeWorkflow,
    fetchQuestions,
    fetchQuestion,
} from "../../../utils/api";
type ViewMode = "list" | "detail";

interface CodeWorkflowsPanelProps {
    onPathChange?: (path: string) => void;
    /* option props particularly useful for unit testing */
    initialViewMode?: ViewMode;
    initialFormState?: Partial<WorkflowFormState>;
}

export type WorkflowQuestionWithClientId = WorkflowQuestion & {
    clientId: string;
}

export interface WorkflowFormState {
    name: string;
    description: string;
    codeType: CodeWorkflowType;
    active: boolean;
    systemQuestions: WorkflowQuestionWithClientId[];
    workflowQuestions: WorkflowQuestionWithClientId[];
}

const generateClientId = () => `workflow-question-${Math.random().toString(36).slice(2)}-${Date.now()}`;
const generateTriggerClientId = () => `trigger-${Math.random().toString(36).slice(2)}-${Date.now()}`;

const withClientIds = (questions: WorkflowQuestion[] | undefined | null): WorkflowQuestionWithClientId[] =>
    (questions ?? []).map((question) => ({
        ...question,
        clientId: (question as WorkflowQuestionWithClientId).clientId ?? generateClientId(),
        triggers: question.triggers?.map((trigger) => ({
            ...trigger,
            _clientId: trigger._clientId ?? generateTriggerClientId(),
        })),
    }));

const initialFormState: WorkflowFormState = {
    name: "",
    description: "",
    codeType: "PROCEDURE",
    active: true,
    systemQuestions: [],
    workflowQuestions: [],
};

const codeTypeOptions: { value: CodeWorkflowType; label: string }[] = [
    { value: "PROCEDURE", label: "Procedure" },
    { value: "MODIFIER", label: "Modifier" },
    { value: "DIAGNOSIS", label: "Diagnosis" },
    { value: "CASE", label: "Case" },
    { value: "SPAN", label: "Span" },
];

// Helper to get code type styling
const getCodeTypeStyle = (codeType: CodeWorkflowType | undefined) => {
    const styles: Record<CodeWorkflowType, { bg: string; color: string }> = {
        PROCEDURE: { bg: "#F3EAF2", color: "#875882" },
        MODIFIER: { bg: "#EAF2FF", color: "#186AE1" },
        DIAGNOSIS: { bg: "#E0FCF0", color: "#26835B" },
        CASE: { bg: "#F3E5F5", color: "#E5927D" },
        SPAN: { bg: "#FFF3E0", color: "#E65100" },
    };
    return styles[codeType ?? "PROCEDURE"] ?? styles.PROCEDURE;
};

// Column configuration for the workflows list - single flowing layout
const workflowColumns: Column<CodeWorkflow>[] = [
    {
        field: "name",
        label: "Name",
        sortable: true,
        width: "flex",
        render: (item) => {
            const style = getCodeTypeStyle(item.codeType as CodeWorkflowType);
            return (
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body1">
                            {item.name}
                        </Typography>
                        <Box
                            sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: "3px",
                                backgroundColor: style.bg,
                                color: style.color,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                textTransform: "capitalize",
                                display: "inline-block",
                            }}
                        >
                            {item.codeType?.toLowerCase()}
                        </Box>
                    </Stack>
                    {item.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {item.description}
                        </Typography>
                    )}
                </Box>
            );
        },
    },
];

const CodeWorkflowsPanel: React.FC<CodeWorkflowsPanelProps> = ({ 
    onPathChange,
    initialViewMode = "list",
    initialFormState: initialFormStateProp,
}) => {
    const [workflows, setWorkflows] = useState<CodeWorkflow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [questionCache, setQuestionCache] = useState<Record<number, Question>>({});
    const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
    const [selectedWorkflow, setSelectedWorkflow] = useState<CodeWorkflow | null>(null);
    const [formState, setFormState] = useState<WorkflowFormState>(() => {
        const allQuestions = withClientIds(initialFormStateProp?.systemQuestions?.concat(initialFormStateProp?.workflowQuestions ?? []) ?? []);
        return {
            ...initialFormState,
            ...initialFormStateProp ?? {},
            systemQuestions: withClientIds(
                allQuestions.filter(q => q.question.questionType === QuestionType.SYSTEM)
            ),
            workflowQuestions: withClientIds(
                allQuestions.filter(q => q.question.questionType !== QuestionType.SYSTEM)
            ),
        };
    });
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
    const [questionsSectionCollapsed, setQuestionsSectionCollapsed] = useState(false);
    const [systemQuestionsSectionCollapsed, setSystemQuestionsSectionCollapsed] = useState(false);

    // Use URL-based list state for pagination, sorting, and search
    const listState = useListState({ defaultSortBy: "name", defaultSortDirection: "asc", paramPrefix: "cw" });
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

    // Apply client-side status filtering
    const filteredWorkflows = useMemo(() => {
        if (statusFilter === "all") return workflows;
        return workflows.filter((item) =>
            statusFilter === "active" ? item.active === true : item.active === false
        );
    }, [workflows, statusFilter]);

    // Combined list for trigger target options (system questions first, then workflow questions)
    const allFormQuestions = useMemo(() => {
        return [...formState.systemQuestions, ...formState.workflowQuestions];
    }, [formState.systemQuestions, formState.workflowQuestions]);


    const availableWorkflowQuestions = useMemo(() => {
        return questions.filter((question) => question.questionType !== QuestionType.SYSTEM);
    }, [questions]);

    const availableSystemQuestions = useMemo(() => {
        return questions.filter((question) => question.questionType === QuestionType.SYSTEM);
    }, [questions]);

    useEffect(() => {
        const path =
            viewMode === "list"
                ? "Workflows"
                : selectedWorkflow
                    ? "Update Workflow"
                    : "Create Workflow";
        onPathChange?.(path);
    }, [viewMode, selectedWorkflow, onPathChange]);

    const loadWorkflows = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await listCodeWorkflowsPaginated({
                page: listState.page,
                size: listState.pageSize,
                sortBy: listState.sortBy,
                sortDirection: listState.sortDirection,
                search: listState.search || undefined,
            });
            setWorkflows(response.content ?? []);
            setTotalCount(response.totalElements);
        } catch (err) {
            console.error("Failed to load workflows", err);
            setError("Unable to load workflows");
        } finally {
            setLoading(false);
        }
    }, [listState.page, listState.pageSize, listState.sortBy, listState.sortDirection, listState.search]);

    const loadQuestions = useCallback(async () => {
        try {
            const list = await fetchQuestions({ active: true });
            setQuestions(list ?? []);
        } catch (err) {
            console.error("Failed to load questions", err);
        }
    }, []);

    useEffect(() => {
        loadQuestions();
    }, [loadQuestions]);

    useEffect(() => {
        loadWorkflows();
    }, [loadWorkflows]);

    const resetForm = () => {
        setFormState({
            ...initialFormState,
            systemQuestions: [],
            workflowQuestions: [],
        });
        setSelectedWorkflow(null);
        setFormError(null);
        setCollapsedSections({});
    };

    const handleCreateNew = () => {
        resetForm();
        setViewMode("detail");
    };

    const handleBackToList = () => {
        setViewMode("list");
        resetForm();
    };

    const ensureQuestionDetail = useCallback(
        async (questionId: number): Promise<Question> => {
            if (questionCache[questionId]) {
                return questionCache[questionId];
            }
            const detail = await fetchQuestion(questionId);
            setQuestionCache((prev) => ({ ...prev, [questionId]: detail }));
            return detail;
        },
        [questionCache]
    );

    const handleAddQuestion = async (questionId: number) => {
        if (!questionId) {
            return;
        }
        try {
            const detail = await ensureQuestionDetail(questionId);
            const isSystemQuestion = detail.questionType === QuestionType.SYSTEM;
            const newQuestion: WorkflowQuestionWithClientId = {
                sequenceOrder: 0,
                question: detail,
                triggers: [],
                clientId: generateClientId(),
            };

            setFormState((prev) => {
                if (isSystemQuestion) {
                    return {
                        ...prev,
                        systemQuestions: [...prev.systemQuestions, newQuestion],
                    };
                }
                return {
                    ...prev,
                    workflowQuestions: [...prev.workflowQuestions, newQuestion],
                };
            });
        } catch (err) {
            console.error("Failed to load question detail", err);
            setFormError("Unable to load question details");
        }
    };

    const handleRemoveQuestion = (clientId: string) => {
        setFormState((prev) => ({
            ...prev,
            systemQuestions: prev.systemQuestions.filter((q) => q.clientId !== clientId),
            workflowQuestions: prev.workflowQuestions.filter((q) => q.clientId !== clientId),
        }));
    };

    const handleSystemQuestionDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination || result.destination.index === result.source.index) {
                return;
            }
            setFormState((prev) => {
                const updated = [...prev.systemQuestions];
                const [moved] = updated.splice(result.source.index, 1);
                if (!moved) {
                    return prev;
                }
                updated.splice(result.destination!.index, 0, moved);
                return { ...prev, systemQuestions: updated };
            });
        },
        []
    );

    const handleWorkflowQuestionDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination || result.destination.index === result.source.index) {
                return;
            }
            setFormState((prev) => {
                const updated = [...prev.workflowQuestions];
                const [moved] = updated.splice(result.source.index, 1);
                if (!moved) {
                    return prev;
                }
                updated.splice(result.destination!.index, 0, moved);
                return { ...prev, workflowQuestions: updated };
            });
        },
        []
    );

    const handleTriggerDragEnd = useCallback(
        (questionClientId: string, sourceIndex: number, destinationIndex: number) => {
            if (sourceIndex === destinationIndex) {
                return;
            }
            setFormState((prev) => {
                const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                    const qIdx = questions.findIndex((q) => q.clientId === questionClientId);
                    if (qIdx === -1) return questions;
                    const question = questions[qIdx];
                    const triggers = question.triggers ? [...question.triggers] : [];
                    const [moved] = triggers.splice(sourceIndex, 1);
                    if (!moved) return questions;
                    triggers.splice(destinationIndex, 0, moved);
                    const reordered = triggers.map((t, idx) => ({ ...t, triggerOrder: idx }));
                    const updated = [...questions];
                    updated[qIdx] = { ...question, triggers: reordered };
                    return updated;
                };
                return {
                    ...prev,
                    systemQuestions: updateQuestions(prev.systemQuestions),
                    workflowQuestions: updateQuestions(prev.workflowQuestions),
                };
            });
        },
        []
    );

    const handleAddTrigger = (clientId: string) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                if (!question.question.answers || question.question.answers.length === 0) {
                    return questions;
                }
                const triggers = question.triggers ? [...question.triggers] : [];
                triggers.push({
                    answerValueIds: [],
                    toQuestionId: undefined,
                    action: WorkflowTriggerAction.GO_TO,
                    triggerOrder: triggers.length,
                    _clientId: generateTriggerClientId(),
                });
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleRemoveTrigger = (clientId: string, triggerIndex: number) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers
                    ? question.triggers
                        .filter((_, idx) => idx !== triggerIndex)
                        .map((t, idx) => ({ ...t, triggerOrder: idx }))
                    : [];
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleTriggerAnswersChange = (clientId: string, triggerIndex: number, values: AnswerValue[]) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers ? [...question.triggers] : [];
                triggers[triggerIndex] = {
                    ...triggers[triggerIndex],
                    answerValueIds: values.map((value) => value.id as number),
                };
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleTriggerTargetChange = (clientId: string, triggerIndex: number, targetId: number | undefined) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers ? [...question.triggers] : [];
                triggers[triggerIndex] = {
                    ...triggers[triggerIndex],
                    toQuestionId: targetId,
                };
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleTriggerToAnswersChange = (clientId: string, triggerIndex: number, answerIds: number[]) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers ? [...question.triggers] : [];
                triggers[triggerIndex] = {
                    ...triggers[triggerIndex],
                    toAnswerValueIds: answerIds,
                };
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleTriggerActionChange = (
        clientId: string,
        triggerIndex: number,
        action: WorkflowTriggerAction
    ) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers ? [...question.triggers] : [];
                const currentTrigger = triggers[triggerIndex] || { answerValueIds: [] };
                const needsTarget = action === "GO_TO" || action === "SET_ANSWERS" || action === "AUTOFILL";
                const needsToAnswers = action === "SET_ANSWERS" || action === "AUTOFILL";
                
                triggers[triggerIndex] = {
                    ...currentTrigger,
                    answerValueIds: currentTrigger.answerValueIds || [],
                    action,
                    toQuestionId: action === "STOP" ? undefined : (needsTarget ? currentTrigger.toQuestionId : undefined),
                    toAnswerValueIds: needsToAnswers ? (currentTrigger.toAnswerValueIds ?? []) : undefined,
                };
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleEditWorkflow = async (workflow: CodeWorkflow) => {
        if (!workflow.id) {
            return;
        }
        setDetailLoading(true);
        setFormError(null);
        try {
            const detail = await fetchCodeWorkflow(workflow.id);
            setSelectedWorkflow(detail);
            const codeType = (detail.codeType as CodeWorkflowType) ?? "PROCEDURE";
            const allQuestions = withClientIds(detail.questions);
            setFormState({
                name: detail.name ?? "",
                description: detail.description ?? "",
                codeType,
                active: detail.active ?? true,
                systemQuestions: allQuestions.filter(q => q.question.questionType === QuestionType.SYSTEM),
                workflowQuestions: allQuestions.filter(q => q.question.questionType !== QuestionType.SYSTEM),
            });
            setViewMode("detail");

            // If the workflow references questions not in the active list, fetch and add them
            const workflowQuestionIds = (detail.questions ?? [])
                .map((wq) => wq.question?.id)
                .filter((id): id is number => id != null);
            const missingQuestionIds = workflowQuestionIds.filter(
                (id) => !questions.some((q) => q.id === id)
            );
            if (missingQuestionIds.length > 0) {
                const inactiveQuestions = await Promise.all(
                    missingQuestionIds.map((id) =>
                        fetchQuestion(id).catch((err) => {
                            console.warn(`Could not fetch inactive question ${id}`, err);
                            return null;
                        })
                    )
                );
                const validQuestions = inactiveQuestions.filter(
                    (q): q is Question => q !== null
                );
                if (validQuestions.length > 0) {
                    setQuestions((prev) => [...prev, ...validQuestions]);
                }
            }
        } catch (err) {
            console.error("Failed to load workflow detail", err);
            setFormError("Unable to load workflow");
        } finally {
            setDetailLoading(false);
        }
    };

    const handleSaveWorkflow = async () => {
        if (!formState.name.trim()) {
            setFormError("Name is required");
            return;
        }
        setSaving(true);
        setFormError(null);
        try {
            // Combine system questions (first) and workflow questions, then assign sequence order
            const allQuestions = [
                ...formState.systemQuestions,
                ...formState.workflowQuestions,
            ].map((q, idx) => ({
                ...q,
                sequenceOrder: idx + 1,
            }));
            
            const questionsPayload: WorkflowQuestion[] = allQuestions.map(({ clientId, ...question }) => ({
                ...question,
            }));
            const payload = {
                name: formState.name.trim(),
                codeType: formState.codeType,
                description: formState.description?.trim() || undefined,
                active: formState.active,
                questions: questionsPayload,                
            };
            if (selectedWorkflow?.id) {
                await updateCodeWorkflow(selectedWorkflow.id, payload);
            } else {
                await createCodeWorkflow(payload);
            }
            await loadWorkflows();
            handleBackToList();
        } catch (err) {
            console.error("Failed to save workflow", err);
            const errorMessage = err instanceof Error && err.message 
                ? `Unable to save workflow: ${err.message}`
                : "Unable to save workflow";
            setFormError(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteWorkflow = async (workflow: CodeWorkflow) => {
        if (!workflow.id) {
            return;
        }
        const confirmed = window.confirm(`Delete code workflow "${workflow.name}"?`);
        if (!confirmed) {
            return;
        }
        try {
            await deleteCodeWorkflow(workflow.id);
            await loadWorkflows();
        } catch (err) {
            console.error("Failed to delete workflow", err);
            setError(err instanceof Error ? err.message : "Unable to delete workflow");
        }
    };

    const handleToggleActive = async (workflow: CodeWorkflow, active: boolean) => {
        if (!workflow.id) {
            return;
        }
        try {
            await updateCodeWorkflow(workflow.id, {
                name: workflow.name,
                codeType: workflow.codeType as CodeWorkflowType,
                description: workflow.description,
                active,
            });
            await loadWorkflows();
        } catch (err) {
            console.error("Failed to toggle workflow active status", err);
            setError(err instanceof Error ? err.message : "Unable to update workflow");
        }
    };

    const renderActions = (workflow: CodeWorkflow) => (
        <>
            <Tooltip title="Edit workflow">
                <IconButton
                    onClick={(event) => {
                        event.stopPropagation();
                        handleEditWorkflow(workflow);
                    }}
                    sx={{ color: "#7498A6" }}
                >
                    <EditOutlined fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete workflow">
                <IconButton
                    onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteWorkflow(workflow);
                    }}
                    sx={{ color: "#7498A6" }}
                >
                    <DeleteOutline fontSize="small" />
                </IconButton>
            </Tooltip>
        </>
    );

    const renderList = () => (
        <RubricManagementList<CodeWorkflow>
            items={filteredWorkflows}
            totalCount={statusFilter === "all" ? totalCount : filteredWorkflows.length}
            loading={loading}
            error={error}
            listState={listState}
            columns={workflowColumns}
            getItemKey={(item) => item.id ?? item.name}
            onItemClick={handleEditWorkflow}
            onAddClick={handleCreateNew}
            searchPlaceholder="Search workflows by name or description..."
            addButtonLabel="Add Workflow"
            itemLabel="workflows"
            emptyMessage="No workflows found."
            renderActions={renderActions}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            onToggleActive={handleToggleActive}
            isItemActive={(item) => Boolean(item.active)}
        />
    );

    const toggleQuestionSection = (questionKey: string) => {
        setCollapsedSections((prev) => ({ ...prev, [questionKey]: !prev[questionKey] }));
    };

    const renderDetail = () => (
        <Grid container gap={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} width="100%" alignItems="center">
                <Button variant={"outlined"} startIcon={<ArrowBack />} onClick={handleBackToList}>
                    Back to Workflows
                </Button>
                <Box flexGrow={1} />
                <Stack direction="row" spacing={1}>
                    <Button variant={'outlined'} onClick={handleBackToList}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSaveWorkflow} disabled={saving}>
                        {saving ? "Saving..." : "Save Workflow"}
                    </Button>
                </Stack>
            </Stack>

            {formError ? <Alert severity="error">{formError}</Alert> : null}

            {detailLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack direction="column" spacing={2} width="100%">
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                required
                                value={formState.name}
                                fullWidth
                                onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                multiline
                                minRows={2}
                                value={formState.description}
                                fullWidth
                                onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel id="code-type-label">Code Type</InputLabel>
                                <Select
                                    labelId="code-type-label"
                                    label="Code Type"
                                    value={formState.codeType}
                                    onChange={(event) => {
                                        const newCodeType = event.target.value as CodeWorkflowType;
                                        setFormState((prev) => {
                                            return {
                                                ...prev,
                                                codeType: newCodeType,
                                            };
                                        });
                                    }}
                                >
                                    {codeTypeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Divider />
                    <Grid container spacing={2} width="100%">
                        <Stack direction="row" spacing={2} alignItems="center" width="100%" justifyContent="space-between">
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>System Questions</Typography>
                            <IconButton
                                onClick={() => setSystemQuestionsSectionCollapsed(!systemQuestionsSectionCollapsed)}
                                size="small"
                            >
                                {systemQuestionsSectionCollapsed ? <ExpandMore /> : <ExpandLess />}
                            </IconButton>
                        </Stack>
                        
                        {!systemQuestionsSectionCollapsed && (
                            <QuestionsSection
                                questions={formState.systemQuestions}
                                validTargetQuestions={allFormQuestions}
                                onDragEnd={handleSystemQuestionDragEnd}
                                collapsedSections={collapsedSections}
                                onToggleSection={toggleQuestionSection}
                                onRemoveQuestion={handleRemoveQuestion}
                                onAddTrigger={handleAddTrigger}
                                onRemoveTrigger={handleRemoveTrigger}
                                onTriggerAnswersChange={handleTriggerAnswersChange}
                                onTriggerTargetChange={handleTriggerTargetChange}
                                onTriggerActionChange={handleTriggerActionChange}
                                onTriggerToAnswersChange={handleTriggerToAnswersChange}
                                onAddQuestion={handleAddQuestion}
                                showAddQuestion={true}
                                questionsAvailableToAdd={availableSystemQuestions}
                                droppableId="system-questions"
                                onTriggerDragEnd={handleTriggerDragEnd}
                            />
                        )}
                    </Grid>                   

                    <Divider />

                     <Grid container spacing={2} width="100%">
                        <Stack direction="row" spacing={2} alignItems="center" width="100%" justifyContent="space-between">
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>Workflow Questions</Typography>
                            <IconButton
                                onClick={() => setQuestionsSectionCollapsed(!questionsSectionCollapsed)}
                                size="small"
                            >
                                {questionsSectionCollapsed ? <ExpandMore /> : <ExpandLess />}
                            </IconButton>
                        </Stack>
                        
                        {!questionsSectionCollapsed && (
                            <QuestionsSection
                                questions={formState.workflowQuestions}
                                validTargetQuestions={formState.workflowQuestions}
                                onDragEnd={handleWorkflowQuestionDragEnd}
                                collapsedSections={collapsedSections}
                                onToggleSection={toggleQuestionSection}
                                onRemoveQuestion={handleRemoveQuestion}
                                onAddTrigger={handleAddTrigger}
                                onRemoveTrigger={handleRemoveTrigger}
                                onTriggerAnswersChange={handleTriggerAnswersChange}
                                onTriggerTargetChange={handleTriggerTargetChange}
                                onTriggerActionChange={handleTriggerActionChange}
                                onTriggerToAnswersChange={handleTriggerToAnswersChange}
                                questionsAvailableToAdd={availableWorkflowQuestions}
                                onAddQuestion={handleAddQuestion}
                                droppableId="workflow-questions"
                                onTriggerDragEnd={handleTriggerDragEnd}
                            />
                        )}
                    </Grid>

                </Stack>
            )}
        </Grid>
    );

    return <Box>{viewMode === "list" ? renderList() : renderDetail()}</Box>;
};

export default CodeWorkflowsPanel;
